import React, {useEffect, useState} from "react";
import {Box, Checkbox, FormControlLabel, Grid, InputAdornment, Link, Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import MainLayout from "./components/MainLayout";
import UserService from "@/services/auth/UserService";
import {object, TypeOf, z} from "zod";
import {useForm} from "react-hook-form";
import WebURL from "@/urls";
import {useTranslation} from "react-i18next";
import {ReactComponent as LayoutImage} from "@/assets/images/UserLogin.svg"
import {useSnackbar} from "notistack";
import {LoadingButton} from "@mui/lab";
import CustomLink from "@/pages/auth/Login/components/CustomLink";

import IconButton from '@mui/material/IconButton';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LogoView from "@/pages/auth/Login/components/LogoView";
import ControlledTextFiled from "@/components/ngteco-hook-form/ControlledTextFiled";
import CommonUtils from "@/utils/CommonUtils";
import {decode as base64Decode, encode as base64Encode} from "base-64";
import {zodResolver} from "@hookform/resolvers/zod";
import CompanyService from "@/services/auth/CompanyService";
import {obtainCompanyInfo} from "@/store/slices/App";
import {useDispatch, useSelector} from "react-redux";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import NGUserAgreement from "@/components/ngteco-form/NGUserAgreement";
import DialogActions from "@mui/material/DialogActions";
import NGPrivacyPolicy from "@/components/ngteco-form/NGPrivacyPolicy";

const UserLogin: React.FC = () => {
  const native = useNavigate();
  const {t, i18n} = useTranslation();
  const loginSchema = object({
    username: z.string({required_error: t("common.This field is required!")})
      .min(1, t("common.This field is required!")).email(t("common.Invalid email format")),
    password: z.string({required_error: t("common.This field is required!")})
      .min(1, t("common.This field is required!")),
    verify_code: z.string().optional(),
  })
  const organs: { id: string, name: string }[] = useSelector((state: any) => state.organ.organs)
  type LoginInput = TypeOf<typeof loginSchema>;
  type Credential = {
    username: string;
    password: string;
    verify_code?: string;
  }
  const [loading, setLoading] = useState<boolean>(false);
  const [autoLogin, setAutoLogin] = useState<boolean>(false);
  const [verifyCode, setVerifyCode] = useState<boolean>(false);
  const [captchaImageUrl, setCaptchaImageUrl] = useState('');
  const [userAgree, setUserAgree] = useState<boolean>(false);
  const [userAgreement, setUserAgreement] = useState<boolean>(false);
  const [privacyPolicy, setPrivacyPolicy] = useState<boolean>(false);
  const [agreementOpen, setAgreementOpen] = React.useState(false);
  const [privacyOpen, setPrivacyOpen] = React.useState(false);
  const {enqueueSnackbar} = useSnackbar()
  const dispatch = useDispatch();
  const loginForm = useForm<LoginInput>({
    defaultValues: { username: "", password: "", verify_code: "" },
    resolver: zodResolver(loginSchema)
  });
  const onLogin = async (credential: Credential) => {
    try {
      if (!userAgree) {
        enqueueSnackbar(t("organ.Please read user agreement and privacy policy before login"), {
          variant: "warning",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        })
        return
      }
      setLoading(true)
      const {status, data} = await UserService.login({...credential, verify: verifyCode})
      if (data.code === 'MUASW0002') {
        setVerifyCode(true)
        return
      } else if (data.code === 'MUASW0003') {
        refreshCaptcha()
        enqueueSnackbar(t("login.captcha error"), {
          variant: "error",
          autoHideDuration: 1500,
          anchorOrigin: {
            vertical: "top",
            horizontal: "center"
          }
        })
        return
      }
      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      if (status < 400) {
        if (autoLogin) {
          // auto login set info to cookies
          CommonUtils.setCookie("autoUserInfo", base64Encode(JSON.stringify(credential)), 5);
        } else {
          CommonUtils.setCookie("autoUserInfo", base64Encode(JSON.stringify(credential)), 0);
        }
        // native(WebURL.AUTH_USER_COMPANY_SELECTION);
        try {
          const def_data = await CompanyService.defaultCompany({});
          if (def_data.status < 400) {
            if (def_data.company_len >= 2) {
              enqueueSnackbar(t("common.success"), {
                variant: "success",
                autoHideDuration: 1500,
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "center"
                }
              })
              native(WebURL.AUTH_USER_COMPANY_SELECTION);
            } else {
              const company_id = def_data?.company_id
              const c_data = await CompanyService.selectCompany({company_id: company_id});
              if (c_data.status < 400) {
                localStorage.setItem('accessToken', c_data.data.access);
                localStorage.setItem('refreshToken', c_data.data.refresh);
                dispatch<any>(obtainCompanyInfo())
                enqueueSnackbar(t("common.success"), {
                  variant: "success",
                  autoHideDuration: 1500,
                  anchorOrigin: {
                    vertical: "top",
                    horizontal: "center"
                  }
                })
                native(WebURL.DASHBOARD)
              } else if (c_data.status == 406) {
                enqueueSnackbar("Selected Role is not linked to your account. Please choose a valid Role.", {
                  variant: "error",
                  autoHideDuration: 1500,
                  anchorOrigin: {
                    vertical: "top",
                    horizontal: "center"
                  }
                })
              }
            }
          }
        } finally {
          setLoading(false);
        }
      } else {
        native(WebURL.AUTH_USER_LOGIN);
      }
    } finally {
      setLoading(false)
    }
  };
  const handleClickOpen = (type:any) => () => {
    if (type==="Agreement") {
      setAgreementOpen(true);
    } else {
      setPrivacyOpen(true)
    }
  };
  const handleClose = (type:any) => {
    if (type==="Agreement") {
      setAgreementOpen(false);
    } else {
      setPrivacyOpen(false)
    }
  };
  useEffect(() => {
    if (userAgreement && privacyPolicy) {
      setUserAgree(true)
    } else {
      setUserAgree(false)
    }
  }, [userAgreement, privacyPolicy])

  const [showPassword, setShowPassword] = useState<boolean>(false);

  const ariaLabel = {'aria-label': 'description'};

  useEffect(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    const _userInfo = CommonUtils.getCookie("autoUserInfo");
    if (_userInfo !== "") {
      const userInfo: Credential = JSON.parse(base64Decode(_userInfo));
      if (userInfo.username && userInfo.password) {
        loginForm.setValue("username", userInfo.username);
        loginForm.setValue("password", userInfo.password);
        setAutoLogin(true);
      }
    }
  }, [])

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword)
  };

  const preventDefault = (event: any) => {
    event.preventDefault();
  }

  // 加载验证码
  const loadCaptcha = async () => {
    try {
      const response = await UserService.getVerifyCode({
        username: loginForm.getValues('username'),
      });
      const blob = new Blob([response], {type: 'image/png'});
      const objectUrl = URL.createObjectURL(blob);
      setCaptchaImageUrl(objectUrl);
    } catch (err) {
      console.error('加载验证码失败:', err);
    }
  };

  // 触发条件并加载
  useEffect(() => {
    if (verifyCode) {
      loadCaptcha();
    }
  }, [verifyCode]);

  // 刷新验证码
  const refreshCaptcha = () => {
    loadCaptcha();
    loginForm.setValue('verify_code', '');
  };

  return (
    <MainLayout image={<LayoutImage/>}>
      <Grid sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #E5E8EC",
        marginTop: "9vh",
        height: "76vh",
        minHeight: "480px",
        width: '34vw',
        borderRadius: "12px",
        // position: "relative",
        backgroundColor: "#ffffff"
      }}>
        <LogoView/>
        <Grid component={"form"} autoComplete="off" style={{width: '28vw', marginTop: "30px"}}>
          <Stack spacing={2}>
            <ControlledTextFiled
              hookForm={loginForm}
              name={'username'}
              label={t("login.email")}
              fullWidth
              required
              placeholder={t("login.email")}
              inputProps={ariaLabel}
            />
            <ControlledTextFiled
              autoComplete="new-password"
              hookForm={loginForm}
              name={'password'}
              label={t("login.password")}
              fullWidth
              required
              onContextMenu={preventDefault}
              // onPaste={preventDefault}
              // onCopy={preventDefault}
              // onCut={preventDefault}
              type={showPassword ? 'text' : 'password'}
              placeholder={t("login.password")}
              inputProps={ariaLabel}
              InputProps={{
                // startAdornment: <InputAdornment position={"start"}><LockOutlined /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    edge="end"
                    onClick={handleClickShowPassword}
                  >
                    {showPassword ? <VisibilityOutlinedIcon/> : <VisibilityOffOutlinedIcon/>}
                  </IconButton>
                </InputAdornment>
              }}
            />
            {verifyCode && (
              <ControlledTextFiled
                hookForm={loginForm}
                name={'verify_code'}
                label={t("login.captcha")}
                fullWidth
                // onChange={(e) => loginForm.setValue('verify_code', e.target.value.toString())}
                placeholder={t("login.captcha")}
                inputProps={ariaLabel}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <img
                        src={captchaImageUrl}
                        alt="captcha"
                        style={{cursor: 'pointer', marginLeft: '10px', verticalAlign: 'middle'}}
                        onClick={refreshCaptcha}
                        title={t('login.refresh captcha')}
                      />
                    </InputAdornment>
                  )
                }}
              />
            )}
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={userAgree}
                    onChange={(event) => setUserAgree(event.target.checked)}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: "8px",
                      maxWidth: "100%",
                      padding: "4px 0",
                    }}
                  >
                    <Typography sx={{
                      color: '#3D3D3D',
                      fontSize: '14px',
                      whiteSpace: 'nowrap',
                    }}>
                      {t("signUp.I have read and agree to the")}
                    </Typography>

                    <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                      <Button
                        sx={{
                          color: '#2BA9DE',
                          minWidth: 'auto',
                          padding: "0 8px",
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={handleClickOpen("Agreement")}
                      >
                        {t("common.User Agreement")}
                      </Button>

                      <Typography sx={{
                        color: '#3D3D3D',
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                      }}>
                        &
                      </Typography>

                      <Button
                        sx={{
                          color: '#2BA9DE',
                          minWidth: 'auto',
                          padding: "0 8px",
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={handleClickOpen("Privacy")}
                      >
                        {t("signUp.Privacy policy")}
                      </Button>
                    </Box>
                  </Box>
                }
              />

              <Dialog
                open={agreementOpen}
                onClose={() => handleClose("Agreement")}
                scroll={"paper"}
                maxWidth={"md"}
                aria-labelledby="scroll-dialog-title"
              >
                <DialogTitle id="scroll-dialog-title">User Agreement</DialogTitle>
                <DialogContent dividers={true}>
                  <DialogContentText tabIndex={-1}>
                    <NGUserAgreement type={'login'}/>
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    handleClose("Agreement");
                    setUserAgreement(false)
                  }}>
                    {t("signUp.Not agree and quit")}
                  </Button>
                  <Button variant="contained" onClick={() => {
                    handleClose("Agreement");
                    setUserAgreement(true);
                  }}>
                    {t("signUp.Read and agree")}
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog
                open={privacyOpen}
                onClose={() => handleClose("Privacy")}
                scroll={"paper"}
                maxWidth={"md"}
                aria-labelledby="scroll-dialog-title"
              >
                <DialogTitle id="scroll-dialog-title">Personal Data Protection and Privacy Policy</DialogTitle>
                <DialogContent dividers={true}>
                  <DialogContentText tabIndex={-1}>
                    <NGPrivacyPolicy type={'login'}/>
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => {
                    handleClose("Privacy");
                    setPrivacyPolicy(false)
                  }}>
                    {t("signUp.Not agree and quit")}
                  </Button>
                  <Button variant="contained" onClick={() => {
                    handleClose("Privacy");
                    setPrivacyPolicy(true);
                  }}>
                    {t("signUp.Read and agree")}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
            <LoadingButton
              type={"submit"}
              fullWidth={true}
              variant={"contained"}
              color={"primary"}
              loading={loading}
              sx={{textTransform: "capitalize", backgroundColor: "#2BA9DE"}}
              onClick={loginForm.handleSubmit(async () => {
                await onLogin(loginForm.getValues())
              })}
            >
              {t("login.login")}
            </LoadingButton>
            <Grid sx={{
              display: "inline-flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <FormControlLabel
                label={
                  <Box>
                    <Typography sx={{fontSize: "14px"}}>
                      {t("login.Remember account in 5 day")}
                    </Typography>
                  </Box>
                }
                control={
                  <Checkbox
                    sx={{color: "#2BA9DE !important"}}
                    color="primary"
                    checked={autoLogin}
                    onChange={(e) => {
                      setAutoLogin(e.target.checked);
                    }}/>
                }
              />
              <Link
                underline={"none"}
                onClick={() => native(WebURL.AUTH_FORGOT_PASSWORD)}
                sx={{
                  cursor: "pointer",
                  padding: "0 8px",
                }}
              >
                <Typography sx={{fontSize: "14px", color: "#2BA9DE"}}>{t("signUp.Forgot password?")}</Typography>
              </Link>
            </Grid>
          </Stack>
        </Grid>
        <Grid sx={{
          display: "inline-flex",
          justifyContent: "center",
          paddingBottom: 1,
          paddingTop: "50px",
          bottom: 0,
          position: "position",
          textAlign: 'center',
          width: '28vw',
        }}>
          <Box><Typography sx={{fontSize: "14px"}}>{t("login.Don't have an account? Please")}</Typography></Box>
          <CustomLink sx={{fontSize: "14px", color: '#2BA9DE'}}
                      label={t("signUp.Sign up")}
                      onClick={() => native(WebURL.AUTH_SIGN_UP)}
          />
        </Grid>
      </Grid>
    </MainLayout>
  )
};
export default UserLogin
